/**
 * Demo-mode payloads for DashboardScreen.
 *
 * Activated by `?demo=1` on the dashboard URL. Produces data shaped
 * exactly like the four /admin/stats/* endpoints so the cards render
 * through their normal happy-path code — no screen-specific branches
 * past the fetcher swap in DashboardScreen.
 *
 * Values are hand-tuned to look plausible (cumulative curves monotone,
 * weekend dips in active users, Hebrew names + varied activity kinds).
 * NOT realistic noise — this is a UI-polish aid, not a stats-review tool.
 */
/** YYYY-MM-DD for the (today - offset) local day. Matches the server's
 *  `DATE(…)` output format so the chart ticks + relative-time formatters
 *  behave identically to the live path. */
function dayStr(offsetDaysAgo) {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() - offsetDaysAgo);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${dd}`;
}
/** ISO string for (now - minutesAgo). Used by the recent-students
 *  timestamps so `formatRelativeTimeHe` resolves to varied phrases. */
function minutesAgoIso(minutesAgo) {
    const d = new Date(Date.now() - minutesAgo * 60000);
    return d.toISOString();
}
export function demoSummary() {
    return {
        kpi: {
            registered_students: 127,
            active_today: 34,
            clip_views_today: 212,
            exams_passed_today: 18,
        },
        // Real grade order (seed.ts): ז׳ → ח׳ → ט׳ → י׳. Counts per spec.
        grades: [
            { id: 1, name: 'כיתה ז׳', students: 32 },
            { id: 2, name: 'כיתה ח׳', students: 28 },
            { id: 3, name: 'כיתה ט׳', students: 24 },
            { id: 4, name: 'כיתה י׳', students: 18 },
        ],
    };
}
export function demoTimeseries() {
    // Hand-shaped cumulative curve for "entries today":
    // slow night (0-5), morning rise (6-11), midday plateau, evening
    // bump (16-22), tapering (23). Ends at 190.
    const hourly = [
        0, 2, 3, 3, 4, 5, // 00-05
        8, 15, 28, 42, 55, 68, // 06-11
        82, 94, 101, 109, 122, 138, // 12-17
        155, 170, 180, 185, 188, 190, // 18-23
    ];
    const entries_today_hourly = hourly.map((cumulative, hour) => ({ hour, cumulative }));
    // 30-day cumulative signups: 40 → 127 with uneven daily steps,
    // monotone. Index 0 == 29-days-ago (oldest) → index 29 == today.
    const totals = [
        40, 42, 45, 45, 48, 51, 52, 54, 58, 61,
        63, 68, 71, 73, 76, 76, 80, 85, 88, 92,
        95, 98, 102, 106, 110, 113, 117, 120, 124, 127,
    ];
    const signups_cumulative = totals.map((total, i) => ({
        date: dayStr(29 - i),
        total,
    }));
    // 30-day daily actives: fluctuates 12-55 with Fri/Sat dips.
    const actives = [
        23, 28, 31, 24, 15, 12, 20, // week -4
        32, 35, 28, 22, 18, 14, 25, // week -3
        34, 38, 42, 36, 29, 20, 16, // week -2
        30, 44, 48, 52, 45, 38, 22, // week -1
        18, 34, // last 2 days
    ];
    const active_users_daily = actives.map((count, i) => ({
        date: dayStr(29 - i),
        count,
    }));
    return { signups_cumulative, active_users_daily, entries_today_hourly };
}
export function demoRecentStudents() {
    // Each entry pairs a Hebrew name + a plausible activity kind + a
    // relative time that exercises a different branch of the Hebrew
    // relative-time formatter ("עכשיו", "לפני דקה", "לפני שעתיים",
    // "אתמול", "שלשום", "לפני שבוע", ...).
    // Demo mode has no real avatars; the recent-students card falls back
    // to the initial-letter circle via the Avatar's children.
    const base = [
        { id: 1001, display_name: 'שרה כהן', email: 'sarah@example.com', grade_name: 'כיתה ח׳',
            last_activity_at: minutesAgoIso(0), last_activity_kind: 'app_open' },
        { id: 1002, display_name: 'יוסי לוי', email: 'yossi@example.com', grade_name: 'כיתה ז׳',
            last_activity_at: minutesAgoIso(8), last_activity_kind: 'clip_view' },
        { id: 1003, display_name: 'מיכל פרץ', email: 'michal@example.com', grade_name: 'כיתה ט׳',
            last_activity_at: minutesAgoIso(25), last_activity_kind: 'passed' },
        { id: 1004, display_name: 'רועי אברהם', email: 'roy@example.com', grade_name: 'כיתה ח׳',
            last_activity_at: minutesAgoIso(60), last_activity_kind: 'attempt' },
        { id: 1005, display_name: 'דנה בן-דוד', email: 'dana@example.com', grade_name: 'כיתה י׳',
            last_activity_at: minutesAgoIso(60 * 2), last_activity_kind: 'progress' },
        { id: 1006, display_name: 'איתי שפירא', email: 'itay@example.com', grade_name: 'כיתה ז׳',
            last_activity_at: minutesAgoIso(60 * 7), last_activity_kind: 'clip_view' },
        { id: 1007, display_name: 'אבישג מזרחי', email: 'avishag@example.com', grade_name: 'כיתה ט׳',
            last_activity_at: minutesAgoIso(60 * 25), last_activity_kind: 'app_open' },
        { id: 1008, display_name: 'נועה אוחיון', email: 'noa@example.com', grade_name: 'כיתה ח׳',
            last_activity_at: minutesAgoIso(60 * 24 * 2), last_activity_kind: 'passed' },
        { id: 1009, display_name: 'ליאור חדד', email: 'lior@example.com', grade_name: 'כיתה י׳',
            last_activity_at: minutesAgoIso(60 * 24 * 4), last_activity_kind: 'progress' },
        { id: 1010, display_name: 'אוריה גולן', email: 'oria@example.com', grade_name: 'כיתה ז׳',
            last_activity_at: minutesAgoIso(60 * 24 * 7), last_activity_kind: 'clip_view' },
    ];
    return { students: base.map((s) => ({ ...s, avatar_url: null })) };
}
export function demoTopLessons() {
    return {
        window_days: 7,
        lessons: [
            { lesson_id: 101, title: 'חיבור וחיסור שברים', views: 1240 },
            { lesson_id: 102, title: 'משוואות ממעלה ראשונה', views: 892 },
            { lesson_id: 103, title: 'משפט פיתגורס', views: 756 },
            { lesson_id: 104, title: 'פתרון בעיות אחוזים', views: 621 },
            { lesson_id: 105, title: 'חישוב שטחים ונפחים', views: 498 },
        ],
    };
}
